import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { UserRound } from "lucide-react";
gsap.registerPlugin(ScrollTrigger);

export default function HowItWorks() {
  const sectionRef = useRef(null);
  const deviceRef = useRef(null);
  const trackRef = useRef(null);
  const slidesRef = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const timelineRef = useRef(null);
  const autoLoopTimeout = useRef(null);

  // --- GSAP Animation Setup ---
  useEffect(() => {
    const ctx = gsap.context(() => {
      const slides = slidesRef.current;
      

      const getSlideWidth = () =>
        deviceRef.current?.clientWidth || deviceRef.current?.offsetWidth || 0;

      const updateSlideWidths = () => {
        const width = getSlideWidth();
        slides.forEach((slide) => (slide.style.width = `${width}px`));
      };
      updateSlideWidths();

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 70%",
          end: "bottom 30%",
          toggleActions: "play pause resume pause",
        },
        repeat: -1,
        repeatDelay: 1.5,
        onRepeat: () => setActiveIndex(0),
      });

      slides.forEach((_, i) => {
        tl.to(trackRef.current, {
          x: () => -i * getSlideWidth(),
          duration: 0.8,
          ease: "power2.inOut",
          onStart: () => setActiveIndex(i),
        }).to({}, { duration: 1.5 });
      });

      timelineRef.current = tl;

      const handleResize = () => {
        updateSlideWidths();
        const p = tl.progress();
        tl.invalidate();
        tl.progress(p);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // --- Manual Navigation ---
  const goToSlide = (index) => {
    const tl = timelineRef.current;
    if (!tl) return;

    const slideWidth =
      deviceRef.current?.clientWidth || deviceRef.current?.offsetWidth || 0;
    tl.pause(); // pause auto loop
    clearTimeout(autoLoopTimeout.current);

    // move instantly to clicked slide
    gsap.to(trackRef.current, {
      x: -index * slideWidth,
      duration: 0.6,
      ease: "power2.inOut",
      onComplete: () => setActiveIndex(index),
    });

    // resume looping after 4 seconds
    autoLoopTimeout.current = setTimeout(() => tl.play(), 4000);
  };

  const slides = [
    {
      title: "Create Your Profile",
      text: "Add traveler details to personalize your lists.",
      content: (
        <div className="flex flex-col items-center justify-center mt-1">
          <UserRound
            size={120}
            strokeWidth={1.8}
            className="text-[#6B91B5] opacity-90 mb-1"
          />
        </div>
      ),
    },
    {
      title: "Enter Trip Details",
      text: "Choose destination, duration, and extras.",
      content: (
        <div className="mt-4 w-2/3 h-40 bg-[#f0f4fa] rounded-xl border border-[#6B91B5]/40 flex items-center justify-center text-[#6B91B5] text-3xl">
          ✈️ 🌍 🧳
        </div>
      ),
    },
    {
      title: "Weather-Aware Insights",
      text: "SmartPackr adapts your list to real forecasts.",
      content: (
        <div className="mt-4 w-2/3 h-40 rounded-xl bg-gradient-to-r from-sky-400 to-indigo-500 text-white flex items-center justify-around text-3xl">
          ☀️ 🌧️ ❄️
        </div>
      ),
    },
    {
      title: "Generate Your List",
      text: "Instantly view your personalized packing checklist.",
      content: (
        <div className="mt-4 w-2/3 h-40 bg-[#e9f5ee] rounded-xl border border-[#6B91B5]/30 flex flex-col justify-center gap-2 p-4 text-left text-gray-700 text-sm">
          <div>☑️ Tops x5</div>
          <div>☑️ Shorts x3</div>
          <div>☑️ Toiletries</div>
          <div>☑️ Power Bank</div>
        </div>
      ),
    },
  ];

  return (
    <section
      id="book"
      ref={sectionRef}
      className="how-it-works bg-[#f9fafb] flex flex-col items-center justify-center text-center px-6 py-24"
    >
      <h2 className="text-3xl sm:text-4xl font-bold mb-6">
        How SmartPackr Works
      </h2>
      <p className="text-gray-600 max-w-xl mb-12">
        See SmartPackr in action — your packing process, simplified.
      </p>

      {/* Phone frame */}
      <div
        ref={deviceRef}
        className="relative w-[280px] h-[540px] sm:w-[320px] sm:h-[580px] rounded-[2rem] border-[8px] border-gray-300 bg-white shadow-2xl overflow-hidden"
      >
        <div
          ref={trackRef}
          className="flex h-full will-change-transform"
          style={{
            transformOrigin: "left center",
            width: "max-content",
          }}
        >
          {slides.map((s, i) => (
            <div
              key={i}
              ref={(el) => (slidesRef.current[i] = el)}
              className="flex-shrink-0 flex flex-col items-center justify-center p-6 box-border"
            >
              <h3 className="text-xl font-semibold text-[#6B91B5] mb-2">
                {s.title}
              </h3>
              <p className="text-sm text-gray-500 max-w-[200px] mb-2">
                {s.text}
              </p>
              {s.content}
            </div>
          ))}
        </div>
      </div>

      {/* Dot indicators */}
      <div className="flex gap-3 mt-6">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goToSlide(i)}
            className={`w-3 h-3 rounded-full transition-all duration-300 focus:outline-none ${
              i === activeIndex
                ? "bg-gradient-to-r from-[#6B91B5] to-[#EE4A72] scale-110"
                : "bg-gray-300 opacity-60 hover:opacity-80"
            }`}
          ></button>
        ))}
      </div>

      <p className="text-gray-500 mt-10 text-sm italic">
        Try SmartPackr free and build your perfect list in minutes.
      </p>
    </section>
  );
}
